// packages/storefront/src/api/fetchLocaleStrings.ts
import type { StorefrontStringIncludes } from '@tourtask/types/api/v1/StorefrontStrings';
import type { Result } from '@tourtask/types/result';
import { publicApiRequest } from './publicApiRequest';
import type { CommonKey } from '@tourtask/i18n/keys';

interface FetchLocaleStringsProps {
  bizSlug: string;
  bizToken: string;
  /** Locale code (e.g. `'en'`, `'es'`) — must be one of `supportedLocales`. */
  lang: string;
  /**
   * Explicit translation keys to fetch: a widget's own list (the chat's, the reviews', the terms'). The
   * storefront sends none and names its list instead (`include.storefront`).
   */
  keys: readonly CommonKey[];
  /**
   * Server-side expansion flags: the storefront's own list, and the key groups whose value space
   * depends on the boot dataset. Setting those here lets this call run in parallel with
   * `fetchBusinessPublicData` instead of having to sequence after it.
   */
  include?: StorefrontStringIncludes;
}

/**
 * Boot-time fetch of the UI translation dictionary, scoped to the
 * specific keys embed renders.
 *
 * Hits `POST /v1/businesses/{bizSlug}/locales/{lang}/strings` with the
 * standard bizToken Authorization header. The response is a
 * `Record<string, string>` containing only matched keys;
 * `LocalizationContext.setStrings` slots it straight in, and missing
 * keys surface as the standard `NO STRING IMPORTED…` placeholder.
 *
 * The biz scope exists for attribution/auth, not content selection —
 * the dictionary content itself does not depend on the business.
 */
export function fetchLocaleStrings({
  bizSlug,
  bizToken,
  lang,
  keys,
  include,
}: FetchLocaleStringsProps): Promise<Result<Record<string, string>>> {
  const path = `businesses/${encodeURIComponent(bizSlug)}/locales/${encodeURIComponent(lang)}/strings`;
  return publicApiRequest<Record<string, string>>({
    path,
    method: 'POST',
    body: { keys, include },
    bizToken,
  }).then((res) => {
    reportUnansweredKeys(keys, res, lang);
    return res;
  });
}

/**
 * Say out loud, at boot, which requested keys the API had no row for.
 *
 * The route answers with matched keys only, so an unanswered key is silent until something
 * renders it — and then it is a `NO STRING IMPORTED WITH VAR NAME` in front of a customer.
 * One log line at boot names every one of them at once, which is how a developer finds out
 * before a customer does. It sees the explicit keys only: the storefront's list, asked for by
 * name, is checked against the catalog by `pnpm i18n:check`, and a key it misses at render
 * throws in development (`LocalizationContext`).
 *
 * Development only: in production this would be noise on a page that is already degraded,
 * and the placeholder is the signal there. Never throws — a partly-translated widget is
 * always better than no widget.
 */
function reportUnansweredKeys(
  requested: readonly CommonKey[],
  res: Result<Record<string, string>>,
  lang: string,
): void {
  if (process.env.NODE_ENV === 'production') return;
  if (!res.success) return;
  const answered = res.data;
  const unanswered = requested.filter((key) => !(key in answered));
  if (unanswered.length === 0) return;
  console.error(
    `[embed i18n] ${unanswered.length} requested key(s) have no ${lang} row and will render ` +
      `as placeholders: ${unanswered.join(', ')}`,
  );
}
